<template>
  <!--
    The `</span\n>{{ " " }}<span` formatting below is deliberate — do not reformat.

    Vue's production build compiles templates with `whitespace: 'condense'`, which
    DELETES whitespace-only text nodes that contain a newline. A normal line break
    between the lead and details spans therefore ships as
    "Contact customer service athelp@wewhistle.com" with no space.

    The space must be an explicit `{{ " " }}` interpolation to survive the build.

    Jest will NOT catch a regression here: vue-jest compiles with the default
    whitespace mode, so the space is preserved in tests either way.
    Verify in a browser against `npm run serve`.
  -->
  <Typography variant="body-1" as="p" :class="['customer-service-contact', layoutClass]">
    <span class="customer-service-contact__lead">{{ leadIn }}</span
    >{{ " "
    }}<span class="customer-service-contact__details">
      <a v-if="showEmail" class="customer-service-contact__link" :href="mailtoHref">{{
        supportEmail
      }}</a
      ><span
        v-if="showEmail && showPhone"
        class="customer-service-contact__separator"
        aria-hidden="true"
      >
        &middot;
      </span>
      <a v-if="showPhone" class="customer-service-contact__link" :href="telHref">{{
        phoneDisplay
      }}</a>
    </span>
  </Typography>
</template>

<script>
import Typography from "@/components/generic/Typography";
import whitelabelMixin from "@/mixins/whitelabelMixin";

/**
 * #188258: the single customer service contact surface.
 *
 * Two data sources, deliberately different:
 *   - email comes from whitelabelMixin, because it is per-tenant.
 *   - phone comes from environment config, because it is the same for every
 *     tenant and is not per-client configurable.
 * That asymmetry is intentional; collapsing them into one mechanism would make
 * the phone per-tenant, which is not what the story asks for.
 *
 * No literal email or phone appears here. A lint rule guards reintroduction of
 * either outside this component.
 *
 * Null handling follows whitelabelMixin's contract: it deliberately passes null
 * through rather than substituting a default, so a seed-data bug stays visible.
 * This component honours that by omitting the email entirely when it is null,
 * rather than rendering a broken `mailto:` or inventing a fallback address.
 */
export const LEAD_IN = "Need help? Contact customer service at";

export default {
  name: "CustomerServiceContact",
  components: { Typography },
  mixins: [whitelabelMixin],
  props: {
    /**
     * `stacked` forces the contact details onto their own line beneath the
     * lead-in. `unstacked` lets them continue the sentence and wrap naturally.
     * Names match the Figma component set variants for Code Connect mapping.
     */
    layout: {
      type: String,
      default: "unstacked",
      validator: (value) => ["stacked", "unstacked"].includes(value),
    },
    /** Which contact details to render. */
    content: {
      type: String,
      default: "both",
      validator: (value) => ["both", "email", "phone"].includes(value),
    },
  },
  computed: {
    leadIn() {
      return LEAD_IN;
    },
    layoutClass() {
      return `customer-service-contact--${this.layout}`;
    },
    phoneDisplay() {
      return process.env.VUE_APP_SUPPORT_PHONE;
    },
    phoneE164() {
      return process.env.VUE_APP_SUPPORT_PHONE_E164;
    },
    showEmail() {
      return ["both", "email"].includes(this.content) && Boolean(this.supportEmail);
    },
    showPhone() {
      return ["both", "phone"].includes(this.content) && Boolean(this.phoneDisplay);
    },
    mailtoHref() {
      return `mailto:${this.supportEmail}`;
    },
    telHref() {
      return `tel:${this.phoneE164}`;
    },
  },
};
</script>

<style scoped>
/*
 * No colour, font, border or shadow is set on the text itself — it inherits the
 * ambient high-emphasis-on-light value, which is what satisfies the story's
 * "no local overrides at the call site" requirement.
 *
 * The links do carry a colour: brandPrimary is the brand token that clears
 * WCAG AA on white (4.60:1), which is what "Brand Primary WCAG Base" denotes.
 */
.customer-service-contact__link {
  color: var(--v-brandPrimary-base);
  text-decoration: underline;
}

/* Stacked pushes the details onto their own line; unstacked leaves them inline
   so the sentence continues and wraps naturally. */
.customer-service-contact--stacked .customer-service-contact__details {
  display: block;
}

.customer-service-contact--unstacked .customer-service-contact__details {
  display: inline;
}
</style>
